import { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import type { Training, CreateTrainingData } from '../../services/trainingLogApi';
import { trainingLogApi } from '../../services/trainingLogApi';

interface Props {
    training?: Training | null;
    onSubmit: (data: CreateTrainingData) => Promise<void>;
    onClose: () => void;
}

const CATEGORIES = ['Technical', 'Language', 'IT', 'Sales', 'Others'];
const CURRENCIES = ['EUR', 'CHF', 'TRY', 'USD'];

function toInputDate(dateStr: string | undefined): string {
    if (!dateStr) return new Date().toISOString().split('T')[0];
    return new Date(dateStr).toISOString().split('T')[0];
}

export function TrainingForm({ training, onSubmit, onClose }: Props) {
    const isEditing = !!training;

    const [startDate, setStartDate] = useState(toInputDate(training?.StartDate));
    const [endDate, setEndDate] = useState(toInputDate(training?.EndDate));
    const [durationHours, setDurationHours] = useState(training?.DurationHours ?? 0);
    const [courseTopic, setCourseTopic] = useState(training?.CourseTopic ?? '');
    const [category, setCategory] = useState(training?.Category ?? 'Technical');
    const [internalExternal, setInternalExternal] = useState(training?.InternalExternal ?? 'External');
    const [trainingOrganizer, setTrainingOrganizer] = useState(training?.TrainingOrganizer ?? '');
    const [isWebTraining, setIsWebTraining] = useState(training?.IsWebTraining ?? false);
    const [costsOriginal, setCostsOriginal] = useState<number | null>(training?.CostsOriginal ?? null);
    const [costsCurrency, setCostsCurrency] = useState(training?.CostsCurrency ?? 'EUR');
    const [exchangeRate, setExchangeRate] = useState<number>(1);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (costsCurrency && costsCurrency !== 'EUR') {
            trainingLogApi.getExchangeRate(costsCurrency)
                .then(r => setExchangeRate(r.exchangeRate))
                .catch(() => setExchangeRate(1));
        } else {
            setExchangeRate(1);
        }
    }, [costsCurrency]);

    const costsEur = costsOriginal != null ? Math.round(costsOriginal * exchangeRate * 100) / 100 : null;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setSubmitting(true);

        try {
            await onSubmit({
                courseTopic,
                category,
                internalExternal,
                startDate,
                endDate,
                durationHours,
                costsOriginal,
                costsCurrency: costsOriginal ? costsCurrency : null,
                costsEur: costsOriginal ? costsEur : null,
                trainingOrganizer: trainingOrganizer || null,
                isWebTraining,
            });
            onClose();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to save training');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between p-4 border-b border-gray-200">
                    <h2 className="text-lg font-semibold text-gray-900">
                        {isEditing ? 'Edit Training' : 'Add Training'}
                    </h2>
                    <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
                        <X className="w-5 h-5 text-gray-500" />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="p-4 space-y-4">
                    {error && (
                        <div className="bg-red-50 text-red-700 text-sm px-3 py-2 rounded border border-red-200">
                            {error}
                        </div>
                    )}

                    {/* Dates & Duration */}
                    <div className="grid grid-cols-3 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                            <input
                                type="date"
                                value={startDate}
                                onChange={e => setStartDate(e.target.value)}
                                required
                                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                            <input
                                type="date"
                                value={endDate}
                                onChange={e => setEndDate(e.target.value)}
                                required
                                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Duration (hours)</label>
                            <input
                                type="number"
                                step="0.5"
                                min="0.5"
                                value={durationHours || ''}
                                onChange={e => setDurationHours(parseFloat(e.target.value) || 0)}
                                required
                                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                        </div>
                    </div>

                    {/* Course Topic */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Course Topic</label>
                        <textarea
                            value={courseTopic}
                            onChange={e => setCourseTopic(e.target.value)}
                            required
                            rows={3}
                            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Describe the training..."
                        />
                    </div>

                    {/* Category */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                        <div className="flex flex-nowrap gap-1.5">
                            {CATEGORIES.map(cat => (
                                <button
                                    key={cat}
                                    type="button"
                                    onClick={() => setCategory(cat)}
                                    className={`px-2.5 py-1.5 rounded text-xs font-medium border transition-colors whitespace-nowrap ${
                                        category === cat
                                            ? 'bg-blue-600 text-white border-blue-600'
                                            : 'bg-white text-gray-600 border-gray-300 hover:border-blue-400'
                                    }`}
                                >
                                    {cat}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Type & Location */}
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                            <div className="flex gap-2">
                                {['Internal', 'External'].map(type => (
                                    <button
                                        key={type}
                                        type="button"
                                        onClick={() => setInternalExternal(type)}
                                        className={`px-4 py-1.5 rounded text-sm font-medium border transition-colors ${
                                            internalExternal === type
                                                ? type === 'Internal' ? 'bg-green-600 text-white border-green-600' : 'bg-orange-500 text-white border-orange-500'
                                                : 'bg-white text-gray-600 border-gray-300 hover:border-gray-400'
                                        }`}
                                    >
                                        {type}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                            <div className="flex gap-2">
                                {[
                                    { label: 'On-site', value: false },
                                    { label: 'Web-based', value: true },
                                ].map(opt => (
                                    <button
                                        key={opt.label}
                                        type="button"
                                        onClick={() => setIsWebTraining(opt.value)}
                                        className={`px-4 py-1.5 rounded text-sm font-medium border transition-colors ${
                                            isWebTraining === opt.value
                                                ? 'bg-blue-600 text-white border-blue-600'
                                                : 'bg-white text-gray-600 border-gray-300 hover:border-gray-400'
                                        }`}
                                    >
                                        {opt.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Organizer */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Training Organizer</label>
                        <input
                            type="text"
                            value={trainingOrganizer}
                            onChange={e => setTrainingOrganizer(e.target.value)}
                            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="e.g. SCP, Vendor Name..."
                        />
                    </div>

                    {/* Costs */}
                    <div className="grid grid-cols-3 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Cost Amount</label>
                            <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={costsOriginal ?? ''}
                                onChange={e => setCostsOriginal(e.target.value ? parseFloat(e.target.value) : null)}
                                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                placeholder="0.00"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
                            <div className="flex flex-wrap gap-1">
                                {CURRENCIES.map(cur => (
                                    <button
                                        key={cur}
                                        type="button"
                                        onClick={() => setCostsCurrency(cur)}
                                        className={`px-2.5 py-1.5 rounded text-xs font-medium border transition-colors ${
                                            costsCurrency === cur
                                                ? 'bg-blue-600 text-white border-blue-600'
                                                : 'bg-white text-gray-600 border-gray-300 hover:border-blue-400'
                                        }`}
                                    >
                                        {cur}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">EUR Equivalent</label>
                            <div className="border border-gray-200 bg-gray-50 rounded-md px-3 py-2 text-sm text-gray-600">
                                {costsEur != null ? `€ ${costsEur.toFixed(2)}` : '-'}
                                {costsCurrency !== 'EUR' && exchangeRate !== 1 && (
                                    <span className="text-xs text-gray-400 ml-1">(rate: {exchangeRate.toFixed(4)})</span>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Actions */}
                    <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={submitting}
                            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
                        >
                            {submitting ? 'Saving...' : isEditing ? 'Save Changes' : 'Add Training'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
